// OAuth Connection Management
// Database-backed connection tracking for email accounts

import { createClient } from '@blinkdotnew/sdk';

// 🔧 CRITICAL FIX: Use authRequired: false for mailbox OAuth operations
// This prevents mailbox OAuth from interfering with the main user authentication
const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false, // Don't force auth - mailbox operations are separate from user login
});

export interface OAuthConnection {
  id: string;
  userId: string;
  provider: 'gmail' | 'outlook';
  email: string;
  connected: boolean;
  connectedAt: string;
  lastSyncAt?: string;
  error?: string;
}

/**
 * Save OAuth connection to database
 * Also saves tokens to secure email_accounts table
 */
export async function saveOAuthConnection(
  userId: string,
  provider: 'gmail' | 'outlook',
  email: string,
  tokens?: {
    accessToken: string;
    refreshToken?: string;
    expiresAt: number;
  }
): Promise<void> {
  try {
    // 🔧 FIX: Match by BOTH userId AND email to support multiple accounts per provider
    // This allows user@gmail.com and another@gmail.com to coexist
    const existing = await blink.db.oauthConnections.list({
      where: {
        userId: userId,
        provider: provider,
        email: email, // ✅ Added email to uniquely identify account
      },
    });

    if (existing && existing.length > 0) {
      // Update existing connection
      await blink.db.oauthConnections.update(existing[0].id, {
        connected: "1",
        connectedAt: new Date().toISOString(),
        error: null,
      });
    } else {
      // Create new connection (allows multiple accounts per provider)
      await blink.db.oauthConnections.create({
        id: `oauth_${provider}_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        userId: userId,
        provider: provider,
        email: email,
        connected: "1",
        connectedAt: new Date().toISOString(),
      });
    }

    // If tokens provided, save to email_accounts table
    if (tokens) {
      // 🔧 FIX: Match by BOTH provider AND email (not just provider)
      const existingAccounts = await blink.db.emailAccounts.list({
        where: {
          userId: userId,
          provider: provider,
          emailAddress: email, // ✅ Added email to support multiple accounts
        },
      });

      const tokenExpiresAt = new Date(tokens.expiresAt).toISOString();

      if (existingAccounts && existingAccounts.length > 0) {
        // Update existing account tokens
        await blink.db.emailAccounts.update(existingAccounts[0].id, {
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken || existingAccounts[0].refreshToken,
          tokenExpiresAt: tokenExpiresAt,
          syncEnabled: "1",
          syncStatus: "idle",
        });
      } else {
        // Create new account (allows multiple Gmail or Outlook accounts)
        await blink.db.emailAccounts.create({
          id: `email_${provider}_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          userId: userId,
          provider: provider,
          emailAddress: email,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken || "",
          tokenExpiresAt: tokenExpiresAt,
          syncEnabled: "1",
          syncStatus: "idle",
        });
      }
    }
    
    console.log(`✅ OAuth connection saved for ${email} (${provider})`);
  } catch (error) {
    console.error('Failed to save OAuth connection:', error);
    throw error;
  }
}

/**
 * Get user's OAuth connections
 */
export async function getUserOAuthConnections(userId: string): Promise<OAuthConnection[]> {
  try {
    const connections = await blink.db.oauthConnections.list({
      where: { userId },
    });

    return (connections || []).map((conn: any) => ({
      id: conn.id,
      userId: conn.userId,
      provider: conn.provider,
      email: conn.email,
      connected: Number(conn.connected) > 0,
      connectedAt: conn.connectedAt,
      lastSyncAt: conn.lastSyncAt,
      error: conn.error,
    }));
  } catch (error) {
    console.error('Failed to get OAuth connections:', error);
    return [];
  }
}

/**
 * Remove OAuth connection - calls backend to revoke tokens
 */
export async function removeOAuthConnection(userId: string, provider: 'gmail' | 'outlook'): Promise<void> {
  try {
    console.log(`Revoking ${provider} connection for user ${userId}...`);
    
    // Call backend function to revoke tokens
    const response = await fetch('https://ben3ou87--revoke-oauth.functions.blink.new', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        provider,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to revoke OAuth connection');
    }

    const result = await response.json();
    console.log(`✅ ${provider} connection revoked:`, result);
  } catch (error) {
    console.error('Failed to remove OAuth connection:', error);
    throw error;
  }
}

/**
 * Update last sync time for connection
 */
export async function updateLastSync(userId: string, provider: 'gmail' | 'outlook'): Promise<void> {
  try {
    const connections = await blink.db.oauthConnections.list({
      where: {
        userId: userId,
        provider: provider,
      },
    });

    if (connections && connections.length > 0) {
      await blink.db.oauthConnections.update(connections[0].id, {
        lastSyncAt: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error('Failed to update last sync:', error);
  }
}

/**
 * Record OAuth connection error
 */
export async function recordOAuthError(
  userId: string,
  provider: 'gmail' | 'outlook',
  error: string
): Promise<void> {
  try {
    const connections = await blink.db.oauthConnections.list({
      where: {
        userId: userId,
        provider: provider,
      },
    });

    if (connections && connections.length > 0) {
      await blink.db.oauthConnections.update(connections[0].id, {
        error: error,
        connected: "0",
      });
    }
  } catch (err) {
    console.error('Failed to record OAuth error:', err);
  }
}
